import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarFooter,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronLeft, ExternalLink, LogOut, ChevronDown } from "lucide-react";
import logoLivemotion from "@/assets/logo-livemotion-hub-square.svg";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  forwardRef,
  useCallback,
  useEffect,
  useState,
  type ComponentPropsWithoutRef,
} from "react";
import { useAuth } from "@/hooks/useAuth";
import { useDevToolsStatus } from "@/hooks/useDevTools";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { EquipmentSidebarTags } from "@/components/equipment/EquipmentSidebarTags";
import {
  mainNavigation,
  mainNavItems,
  type MainNavItem,
  type MainNavSubItem,
} from "@/navigation/mainNav";
import { matchContext } from "@/navigation/contexts";
import { ContextNavigation } from "@/navigation/ContextNavigation";
import { useContextNav } from "@/navigation/useContextNav";
import type { MatchedContext } from "@/navigation/types";
import { useFileArchiveMode } from "@/lib/file-archive";
import { useFavoriteTools } from "@/hooks/useFavoriteTools";
import { TOOLS_SECTION_TITLE, type Tool } from "@/navigation/tools";

/** Group-specific extras rendered after a group's sub-items. */
const EXTRA_SUB_ITEMS: Record<string, MainNavItem["extraSubItems"]> = {
  Equipment: EquipmentSidebarTags,
};

function isSubItemActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

/** Title of the navigation group whose sub-items contain `pathname`. */
function activeGroupTitle(pathname: string) {
  return mainNavItems.find((item) =>
    item.items?.some((subItem) => isSubItemActive(pathname, subItem.href)),
  )?.title;
}

/** Receives the menu button's classes via `asChild`, so it forwards props. */
const SubItemLink = forwardRef<
  HTMLAnchorElement,
  ComponentPropsWithoutRef<"a"> & {
    subItem: MainNavSubItem | Tool;
    onNavigate: () => void;
  }
>(function SubItemLink({ subItem, onNavigate, ...props }, ref) {
  const externalHref =
    subItem.externalHref?.() ?? (subItem.external ? subItem.href : undefined);
  if (externalHref) {
    return (
      <a
        ref={ref}
        {...props}
        href={externalHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
      >
        <subItem.icon className="h-4 w-4" />
        <span>{subItem.title}</span>
        <ExternalLink className="ml-auto h-3 w-3 shrink-0 text-muted-foreground" />
      </a>
    );
  }
  return (
    <NavLink ref={ref} {...props} to={subItem.href} end onClick={onNavigate}>
      <subItem.icon className="h-4 w-4" />
      <span>{subItem.title}</span>
    </NavLink>
  );
});

/** Icon-rail flyout for a group of the main navigation (L8). */
function CollapsedGroup({
  item,
  items,
  isActive,
}: {
  item: MainNavItem;
  items: MainNavSubItem[];
  isActive: boolean;
}) {
  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton isActive={isActive} aria-label={item.title}>
                <item.icon className="h-4 w-4" />
                <span>{item.title}</span>
              </SidebarMenuButton>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">{item.title}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent side="right" align="start">
          <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {items.map((subItem) => {
            const externalHref =
              subItem.externalHref?.() ??
              (subItem.external ? subItem.href : undefined);
            return (
              <DropdownMenuItem key={subItem.href} asChild>
                {externalHref ? (
                  <a
                    href={externalHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <subItem.icon className="h-4 w-4 mr-2" />
                    {subItem.title}
                  </a>
                ) : (
                  <NavLink to={subItem.href} end>
                    <subItem.icon className="h-4 w-4 mr-2" />
                    {subItem.title}
                  </NavLink>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

function MainNavigation({ onNavigate }: { onNavigate: () => void }) {
  const location = useLocation();
  // Re-render when the remembered file archive address changes (externalHref).
  useFileArchiveMode();
  const { isEmployee, role } = useAuth();
  const devToolsStatus = useDevToolsStatus();
  const { favoriteTools } = useFavoriteTools();
  const { state, isMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  // Groups start closed; only the group containing the current page opens,
  // also when navigating into it from elsewhere.
  const activeGroup = activeGroupTitle(location.pathname);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    activeGroup ? { [activeGroup]: true } : {},
  );
  useEffect(() => {
    if (activeGroup) {
      setOpenGroups((prev) =>
        prev[activeGroup] ? prev : { ...prev, [activeGroup]: true },
      );
    }
  }, [activeGroup]);

  const isActive = (href: string) => location.pathname === href;
  const isGroupActive = (items?: { href: string }[]) =>
    items?.some((item) => isSubItemActive(location.pathname, item.href)) ??
    false;

  const isVisible = (entry: {
    employeeOnly?: boolean;
    adminOnly?: boolean;
    devToolsOnly?: boolean;
  }) =>
    (!entry.employeeOnly || isEmployee) &&
    (!entry.adminOnly || role === "admin") &&
    (!entry.devToolsOnly || !!devToolsStatus.data?.enabled);

  const renderItem = (item: MainNavItem) => {
    if (item.items) {
      const filteredItems = item.items.filter(isVisible);

      if (filteredItems.length === 0) return null;
      const Extra = EXTRA_SUB_ITEMS[item.title];

      if (collapsed) {
        return (
          <CollapsedGroup
            key={item.title}
            item={item}
            items={filteredItems}
            isActive={isGroupActive(filteredItems)}
          />
        );
      }

      return (
        <Collapsible
          key={item.title}
          open={openGroups[item.title]}
          onOpenChange={(open) =>
            setOpenGroups((prev) => ({
              ...prev,
              [item.title]: open,
            }))
          }
        >
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className={cn(
                "w-full justify-between",
                isGroupActive(filteredItems) &&
                  "bg-sidebar-accent text-sidebar-accent-foreground",
              )}
            >
              <NavLink
                to={filteredItems[0].href}
                onClick={() => {
                  setOpenGroups((prev) => ({
                    ...prev,
                    [item.title]: true,
                  }));
                  onNavigate();
                }}
              >
                <span className="flex items-center gap-2">
                  <item.icon className="h-4 w-4" />
                  {item.title}
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    openGroups[item.title] && "rotate-180",
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpenGroups((prev) => ({
                      ...prev,
                      [item.title]: !prev[item.title],
                    }));
                  }}
                />
              </NavLink>
            </SidebarMenuButton>
            <CollapsibleContent>
              <SidebarMenuSub>
                {filteredItems.map((subItem) => (
                  <SidebarMenuSubItem key={subItem.href}>
                    <SidebarMenuSubButton
                      asChild
                      className={cn(
                        isActive(subItem.href) &&
                          "bg-sidebar-accent text-primary font-medium",
                      )}
                    >
                      <SubItemLink subItem={subItem} onNavigate={onNavigate} />
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
                {Extra && <Extra onNavigate={onNavigate} />}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton
          asChild
          tooltip={item.title}
          className={cn(
            isActive(item.href!) &&
              "bg-sidebar-accent text-primary font-medium",
          )}
        >
          <NavLink to={item.href!} end onClick={onNavigate}>
            <item.icon className="h-4 w-4" />
            <span>{item.title}</span>
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <SidebarContent className="scrollbar-thin" data-testid="main-navigation">
      {mainNavigation.map((section) => {
        const items = section.items.filter(isVisible);
        if (items.length === 0) return null;
        return (
          <SidebarGroup key={section.title}>
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground">
              {section.title}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map(renderItem)}
                {section.title === TOOLS_SECTION_TITLE &&
                  favoriteTools.map((tool) => (
                    <SidebarMenuItem key={tool.id}>
                      <SidebarMenuButton
                        asChild
                        tooltip={tool.title}
                        className={cn(
                          isActive(tool.href) &&
                            "bg-sidebar-accent text-primary font-medium",
                        )}
                      >
                        <SubItemLink subItem={tool} onNavigate={onNavigate} />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        );
      })}
    </SidebarContent>
  );
}

/** Link back from the main menu peek to the context the user is in. */
function BackToContext({
  matched,
  onClick,
}: {
  matched: MatchedContext;
  onClick: () => void;
}) {
  const location = useLocation();
  const { header } = useContextNav(matched, location.pathname);
  return (
    <div className="border-b border-sidebar-border p-2">
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            onClick={onClick}
            tooltip={`Back to ${header.title ?? matched.def.typeLabel}`}
            data-testid="back-to-context"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="truncate">
              Back to {header.title ?? matched.def.typeLabel}
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </div>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const { user, isEmployee, signOut } = useAuth();
  const { isMobile, setOpenMobile, state } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const matched = matchContext(location.pathname);
  // The main menu can be shown while inside a context (L7); leaving the
  // context or navigating resets it.
  const [showMainMenu, setShowMainMenu] = useState(false);
  useEffect(() => {
    setShowMainMenu(false);
  }, [matched?.key]);

  const closeMobileSidebar = useCallback(() => {
    setShowMainMenu(false);
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, setOpenMobile]);

  const userInitials = user?.email?.slice(0, 2).toUpperCase() || "U";
  const showContext = matched && !showMainMenu;

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-sidebar-border"
      data-testid="app-sidebar"
      data-mode={showContext ? "context" : "main"}
    >
      {showContext ? (
        <ContextNavigation
          key={matched.def.type}
          matched={matched}
          onShowMainMenu={() => setShowMainMenu(true)}
          onNavigate={closeMobileSidebar}
        />
      ) : (
        <>
          <SidebarHeader className="border-b border-sidebar-border p-4 group-data-[collapsible=icon]:p-2">
            <Link
              to="/"
              onClick={closeMobileSidebar}
              className="flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring"
              aria-label="Dashboard"
              data-testid="sidebar-home-link"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sidebar-accent overflow-hidden group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8">
                <img
                  src={logoLivemotion}
                  alt="LiveMotion"
                  className="h-full w-full object-cover"
                />
              </div>
              {!collapsed && (
                <div>
                  <h1 className="text-sm font-semibold text-sidebar-foreground">
                    LM Hub
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    Project Management
                  </p>
                </div>
              )}
            </Link>
          </SidebarHeader>
          {matched && (
            <BackToContext
              key={matched.def.type}
              matched={matched}
              onClick={() => setShowMainMenu(false)}
            />
          )}
          <MainNavigation onNavigate={closeMobileSidebar} />
        </>
      )}

      <SidebarFooter className="border-t border-sidebar-border p-4 group-data-[collapsible=icon]:p-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            {!collapsed && (
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{user?.email}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {isEmployee ? "Employee" : "Freelancer"}
                </p>
              </div>
            )}
          </div>
          {!collapsed && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-foreground shrink-0"
              onClick={signOut}
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
